// ===============================
// Meta Ads UTM Tracking Utilities
// ===============================

export interface UtmParams {
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_content: string
  utm_term: string // Meta sends ad set/targeting info here
  utm_id: string // Meta campaign ID
  fbclid: string
}

const UTM_STORAGE_KEY = 'meta_ads_utm_data'

/**
 * Captures UTM parameters from URL and persists them to localStorage
 * Only updates if UTM parameters are present in the URL
 * Safe to call multiple times - won't overwrite existing data if no UTMs in URL
 */
export function captureUtmParams(): void {
  try {
    const urlParams = new URLSearchParams(window.location.search)

    // Check if any UTM parameters exist in URL
    const hasUtmParams =
      urlParams.has('utm_source') ||
      urlParams.has('utm_medium') ||
      urlParams.has('utm_campaign') ||
      urlParams.has('utm_content') ||
      urlParams.has('utm_term') ||
      urlParams.has('utm_id') ||
      urlParams.has('fbclid')

    // If no UTM params in URL, don't overwrite existing data
    if (!hasUtmParams) {
      return
    }

    // Get existing data or create new object
    const existingData = getUtmParams()

    // Update only the parameters that are present in URL
    const updatedData: UtmParams = {
      utm_source: urlParams.get('utm_source') || existingData.utm_source,
      utm_medium: urlParams.get('utm_medium') || existingData.utm_medium,
      utm_campaign: urlParams.get('utm_campaign') || existingData.utm_campaign,
      utm_content: urlParams.get('utm_content') || existingData.utm_content,
      utm_term: urlParams.get('utm_term') || existingData.utm_term,
      utm_id: urlParams.get('utm_id') || existingData.utm_id,
      fbclid: urlParams.get('fbclid') || existingData.fbclid,
    }

    // Store in localStorage
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(updatedData))

    console.log('✅ UTM parameters captured:', updatedData)
  } catch (error) {
    console.error('❌ Error capturing UTM parameters:', error)
  }
}

/**
 * Retrieves stored UTM parameters from localStorage
 * Returns empty strings for missing values (never null/undefined)
 */
export function getUtmParams(): UtmParams {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY)

    if (stored) {
      const parsed = JSON.parse(stored)
      // Ensure all fields exist with empty string defaults
      return {
        utm_source: parsed.utm_source || '',
        utm_medium: parsed.utm_medium || '',
        utm_campaign: parsed.utm_campaign || '',
        utm_content: parsed.utm_content || '',
        utm_term: parsed.utm_term || '',
        utm_id: parsed.utm_id || '',
        fbclid: parsed.fbclid || '',
      }
    }
  } catch (error) {
    console.error('❌ Error retrieving UTM parameters:', error)
  }

  // Return empty object if nothing stored or error
  return {
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    utm_id: '',
    fbclid: '',
  }
}

/**
 * Clears stored UTM parameters (useful for testing)
 */
export function clearUtmParams(): void {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY)
    console.log('✅ UTM parameters cleared')
  } catch (error) {
    console.error('❌ Error clearing UTM parameters:', error)
  }
}
